import * as React from 'react';
import i18next from 'i18next';
import { LANGUAGES, ResourceService } from '@ixicare/resources';

import getNavigatorLanguage from './utils/getBrowserLanguage';

const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);

const loadTranslations = () => {
  const modules = import.meta.glob('./data/*.json', { eager: true }) as Record<string, { default: any }>;
  const translations: Record<string, any> = {};

  for (const code of LANGUAGE_CODES) {
    const mod = modules[`./data/${code}.json`];
    if (mod) {
      translations[code] = mod.default;
    }
  }

  return translations;
};

const supportedLanguages = loadTranslations();

const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'hcp-dashboard.language';
const LANGUAGE_PARAM = 'lang';

// Map a requested code onto one we actually ship: exact match first, then the
// base subtag so `nl-BE` still lands on `nl`.
const toSupportedLanguage = (code?: string | null): string | undefined => {
  if (!code) return undefined;
  const lower = code.toLowerCase();
  if (supportedLanguages[lower]) return lower;
  const base = lower.split('-')[0];
  return supportedLanguages[base] ? base : undefined;
};

const resolveBrowserLanguage = (): string => toSupportedLanguage(getNavigatorLanguage()) ?? DEFAULT_LANGUAGE;

// Shared links (invites, password resets) can pin the UI language with `?lang=nl`.
// Read straight from location.search rather than the router: this evaluates
// before React mounts, so the very first paint is already in the right locale.
const resolveUrlLanguage = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return toSupportedLanguage(new URLSearchParams(window.location.search).get(LANGUAGE_PARAM));
};

const resolveInitialLanguage = (): string => {
  const fromUrl = resolveUrlLanguage();
  if (fromUrl) return fromUrl;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && supportedLanguages[stored]) return stored;
  } catch {
    // localStorage may be unavailable (private mode, etc.) — fall through.
  }
  return resolveBrowserLanguage();
};

export const getCurrentLanguage = (): string => i18next.language || resolveInitialLanguage();

const resources: Record<string, { translation: any }> = {};
for (const [code, data] of Object.entries(supportedLanguages)) {
  resources[code] = { translation: data };
}

i18next.init({
  resources,
  lng: resolveInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
});

ResourceService.setLanguage(i18next.language);

// Switch the active UI language and sync the Accept-Language header for API
// calls. The chosen code is cached in localStorage — that cache is the source
// of truth for the dashboard, so the next page load reads it back before any
// module-scope `t(...)` evaluates.
export const setLanguage = (code?: string | null) => {
  const next = code && supportedLanguages[code] ? code : resolveBrowserLanguage();
  if (i18next.language !== next) {
    i18next.changeLanguage(next);
  }
  ResourceService.setLanguage(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore — non-critical persistence.
  }
};

// An invited user navigates off the `?lang=` URL as soon as the flow moves on
// (accept invite -> set password -> dashboard), and the param does not survive
// client-side navigation. Persist it once at startup so the whole flow stays in
// the language the link asked for.
const urlLanguage = resolveUrlLanguage();
if (urlLanguage) {
  setLanguage(urlLanguage);
}

// Docs-screenshot hook. Lets the help-center Playwright flow switch the UI
// language in-place — no reload, no profile write — so it can capture the same
// screen in every locale. Behaviourally identical to the in-app language
// picker, so safe to ship in the production build.
if (typeof window !== 'undefined') {
  (window as unknown as { __ixiSetLanguage?: (code: string) => void }).__ixiSetLanguage = (code: string) =>
    setLanguage(code);
}

export const hasStoredLanguage = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return !!(stored && supportedLanguages[stored]);
  } catch {
    return false;
  }
};

export const t = (key: string, properties?: any): string => {
  let translation = i18next.t(key, properties) as string;
  translation = translation.replace(/\<\/?b\>/gi, '').replace(/\<\/?strong\>/gi, '');
  return translation;
};
export const tHtml = (key: string, properties?: any): any => {
  return <span dangerouslySetInnerHTML={{ __html: i18next.t(key, properties) }} />;
};
export default i18next;
